import React, { useState, useCallback, useEffect, useRef } from 'react';
import RichTextEditor from '../components/RichTextEditor';
import { kbAPI } from '../services/api';

// Colored accent dots for notebook categories (cycles through palette)
const NOTE_COLORS = [
    '#0EBCD4', '#4ECDC4', '#FFB347', '#A78BFA',
    '#60A5FA', '#F87171', '#34D399', '#FBBF24',
    '#F472B6', '#FB923C',
];

function mergeKbTables(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    let changed = true;
    while (changed) {
        changed = false;
        const tables = tmp.querySelectorAll('table');
        for (const tbl of tables) {
            let sib = tbl.nextSibling;
            while (sib && sib.nodeType === Node.TEXT_NODE && !sib.textContent.trim()) sib = sib.nextSibling;
            if (sib && sib.nodeType === Node.ELEMENT_NODE && sib.nodeName === 'P' && !sib.textContent.trim()) {
                const afterP = sib.nextSibling;
                let nextTbl = afterP;
                while (nextTbl && nextTbl.nodeType === Node.TEXT_NODE && !nextTbl.textContent.trim()) nextTbl = nextTbl.nextSibling;
                if (nextTbl && nextTbl.nodeName === 'TABLE') {
                    const tbody = tbl.querySelector('tbody') || tbl;
                    const srcTbody = nextTbl.querySelector('tbody') || nextTbl;
                    for (const tr of Array.from(srcTbody.querySelectorAll('tr'))) tbody.appendChild(tr);
                    sib.remove();
                    nextTbl.remove();
                    changed = true;
                    break;
                }
            }
        }
    }
    return tmp.innerHTML;
}

function printArticle(article, isRecipe) {
    const cat = article.categoryName ? `<span class="meta-pill">${article.categoryName}</span>` : '';
    const draft = article.status === 'draft' ? '<span class="meta-pill draft">Draft</span>' : '';
    const author = article.authorName || 'Unknown';
    const updated = article.updatedAt ? new Date(article.updatedAt).toLocaleString() : '';

    let recipeMeta = '';
    if (isRecipe) {
        const parts = [];
        if (article.prepTime) parts.push(`Prep: ${article.prepTime}`);
        if (article.cookTime) parts.push(`Cook: ${article.cookTime}`);
        if (article.servings) parts.push(`Serves: ${article.servings}`);
        if (parts.length) recipeMeta = `<div class="recipe-meta">${parts.join(' &nbsp;·&nbsp; ')}</div>`;
    }

    const escHtml = (s) => String(s || '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]);
    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${escHtml(article.title)}</title>
<style>
  body{font-family:Georgia,"Times New Roman",serif;color:#1f2937;max-width:7.5in;margin:.5in auto;padding:0 .25in;font-size:12pt;line-height:1.6}
  .meta{font-family:system-ui,sans-serif;font-size:10pt;color:#6b7280;margin-bottom:18px;padding-bottom:14px;border-bottom:1px solid #e5e7eb}
  .meta-pill{display:inline-block;font-family:system-ui,sans-serif;font-size:9pt;padding:2px 8px;background:#f3f4f6;color:#374151;border-radius:10px;margin-right:6px}
  .meta-pill.draft{background:#fef3c7;color:#92400e}
  h1.kb-title{font-size:26pt;font-weight:700;line-height:1.2;margin:0 0 8px;color:#111827}
  .recipe-meta{font-family:system-ui,sans-serif;font-size:10pt;color:#6b7280;margin-bottom:12px}
  .content{font-size:12pt;line-height:1.7}
  .content h1{font-size:20pt;margin-top:18px}.content h2{font-size:16pt;margin-top:16px}.content h3{font-size:13pt;margin-top:14px}
  .content p{margin:0 0 10px}
  .content li[data-list]{list-style:none;position:relative;padding-left:1.8em}
  .content li[data-list="bullet"]::before{content:'\\2022';position:absolute;left:.4em}
  .content li[data-list="ordered"]{counter-increment:list-0}
  .content li[data-list="ordered"]::before{content:counter(list-0,decimal)'. ';position:absolute;left:0}
  @page{margin:.5in}@media print{body{margin:0}}
</style></head><body>
<div>${cat}${draft}</div>
<h1 class="kb-title">${escHtml(article.title)}</h1>
${recipeMeta}
<div class="meta">By ${author} &middot; Updated ${updated}</div>
<div class="content rich-text-content">${article.content || '<p><em>Empty.</em></p>'}</div>
</body></html>`;
    // eslint-disable-next-line no-useless-concat
    const printScript = '<scr' + 'ipt>window.onload=function(){setTimeout(function(){window.print();},200);}</scr' + 'ipt>';
    const w = window.open('', '_blank');
    if (!w) { alert('Pop-up blocked. Please allow pop-ups to print.'); return; }
    w.document.write(html.replace('</body>', printScript + '</body>'));
    w.document.close();
}

function KnowledgeBaseView({ isRecipe = false }) {
    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeNote, setActiveNote] = useState(null); // full note object
    const [activeNoteLoading, setActiveNoteLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editStatus, setEditStatus] = useState('published');
    const [editCategoryId, setEditCategoryId] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [isNewNote, setIsNewNote] = useState(false);
    // mobile drill-down: 'notebooks' | 'list' | 'editor'
    const [mobilePanel, setMobilePanel] = useState('notebooks');
    const saveTimerRef = useRef(null);
    const userEditedRef = useRef(false);

    const listCategories = useCallback(() =>
        isRecipe ? kbAPI.listRecipeCategories() : kbAPI.listCategories(), [isRecipe]);
    const listArticles = useCallback((params) =>
        isRecipe ? kbAPI.listRecipes(params) : kbAPI.listArticles(params), [isRecipe]);
    const getArticle = useCallback((id) =>
        isRecipe ? kbAPI.getRecipe(id) : kbAPI.getArticle(id), [isRecipe]);
    const deleteArticleFn = useCallback((id) =>
        isRecipe ? kbAPI.deleteRecipe(id) : kbAPI.deleteArticle(id), [isRecipe]);

    const loadCategories = useCallback(async () => {
        try { setCategories((await listCategories()).data); } catch (err) { console.error(err); }
    }, [listCategories]);

    const loadArticles = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCategoryId !== 'all') params.categoryId = selectedCategoryId;
            if (search.trim()) params.search = search.trim();
            const res = await listArticles(params);
            setArticles(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [listArticles, selectedCategoryId, search]);

    useEffect(() => { loadCategories(); }, [loadCategories]);
    useEffect(() => { loadArticles(); }, [loadArticles]);

    const openNote = async (id) => {
        setActiveNoteLoading(true);
        setEditing(false);
        setIsNewNote(false);
        setSaveError('');
        setMobilePanel('editor');
        userEditedRef.current = false;
        try {
            const a = (await getArticle(id)).data;
            setActiveNote(a);
            setEditTitle(a.title || '');
            setEditContent(a.content || '');
            setEditStatus(a.status || 'published');
            setEditCategoryId(a.categoryId || '');
        } catch (err) { console.error(err); }
        finally { setActiveNoteLoading(false); }
    };

    const startNewNote = () => {
        setActiveNote(null);
        setEditing(true);
        setIsNewNote(true);
        setSaveError('');
        setMobilePanel('editor');
        userEditedRef.current = false;
        setEditTitle('');
        setEditContent('');
        setEditStatus('published');
        setEditCategoryId(selectedCategoryId !== 'all' ? String(selectedCategoryId) : '');
    };

    const saveNote = async (overrideStatus, { silent = false } = {}) => {
        if (!editTitle.trim()) { setSaveError('Title is required'); return; }
        setSaving(true);
        setSaveError('');
        try {
            const payload = {
                title: editTitle.trim(),
                categoryId: editCategoryId ? Number(editCategoryId) : null,
                content: mergeKbTables(editContent),
                status: overrideStatus || editStatus,
            };
            let id;
            if (isNewNote || !activeNote) {
                const res = isRecipe ? await kbAPI.createRecipe(payload) : await kbAPI.createArticle(payload);
                id = res.data.id;
                setIsNewNote(false);
            } else {
                id = activeNote.id;
                if (isRecipe) await kbAPI.updateRecipe(id, payload);
                else await kbAPI.updateArticle(id, payload);
            }
            await loadArticles();
            await loadCategories();
            const refreshed = (await getArticle(id)).data;
            setActiveNote(refreshed);
            if (!silent) {
                setEditing(false);
                setMobilePanel('editor');
            }
        } catch (err) {
            setSaveError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!activeNote) return;
        if (!window.confirm('Delete this note? This cannot be undone.')) return;
        try {
            await deleteArticleFn(activeNote.id);
            setActiveNote(null);
            setEditing(false);
            setIsNewNote(false);
            setMobilePanel('list');
            await loadArticles();
            await loadCategories();
        } catch (err) { alert('Delete failed: ' + err.message); }
    };

    const cancelEdit = () => {
        if (isNewNote) {
            setActiveNote(null);
            setIsNewNote(false);
            setMobilePanel('list');
        } else {
            setMobilePanel('editor');
        }
        setEditing(false);
        setSaveError('');
        if (activeNote) {
            setEditTitle(activeNote.title || '');
            setEditContent(activeNote.content || '');
            setEditStatus(activeNote.status || 'published');
            setEditCategoryId(activeNote.categoryId || '');
        }
    };

    // Auto-save on title/content change while editing (debounced, only for existing notes after user has typed)
    useEffect(() => {
        if (!editing || isNewNote || !userEditedRef.current) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            if (editTitle.trim()) saveNote(undefined, { silent: true });
        }, 2000);
        return () => clearTimeout(saveTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTitle, editContent]);

    const allCats = categories;
    const parentCats = allCats.filter(c => !c.parentId);
    const childrenOf = (pid) => allCats.filter(c => c.parentId === pid);

    const noun = isRecipe ? 'Recipe' : 'Note';
    const sectionTitle = isRecipe ? 'Recipes' : 'Notes';

    const filteredArticles = articles;

    // Build flat category list with index for coloring
    const flatCats = [];
    parentCats.forEach((p, i) => {
        flatCats.push({ ...p, colorIdx: i });
        childrenOf(p.id).forEach((k, j) => {
            flatCats.push({ ...k, colorIdx: i, isChild: true });
        });
    });

    const backArrow = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"/>
        </svg>
    );

    return (
        <div className="notes-shell fade-in" data-mobile-panel={mobilePanel}>
            {/* ── Panel 1: Notebooks (categories) ── */}
            <aside className="notes-notebooks">
                <div className="notes-notebooks-header">
                    <span className="notes-notebooks-title">{sectionTitle}</span>
                </div>
                <nav className="notes-notebooks-nav">
                    <button
                        className={`notes-notebook-item ${selectedCategoryId === 'all' ? 'active' : ''}`}
                        onClick={() => { setSelectedCategoryId('all'); setActiveNote(null); setEditing(false); setMobilePanel('list'); }}
                    >
                        <span className="notes-notebook-dot" style={{ background: '#0EBCD4' }} />
                        <span className="notes-notebook-label">All {sectionTitle}</span>
                        <span className="notes-notebook-count">{articles.length}</span>
                    </button>

                    {flatCats.map((cat) => (
                        <button
                            key={cat.id}
                            className={`notes-notebook-item ${cat.isChild ? 'notes-notebook-child' : ''} ${String(selectedCategoryId) === String(cat.id) ? 'active' : ''}`}
                            onClick={() => { setSelectedCategoryId(cat.id); setActiveNote(null); setEditing(false); setMobilePanel('list'); }}
                        >
                            <span className="notes-notebook-dot" style={{ background: NOTE_COLORS[cat.colorIdx % NOTE_COLORS.length] }} />
                            <span className="notes-notebook-label">{cat.name}</span>
                            <span className="notes-notebook-count">{cat.articleCount}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* ── Panel 2: Note list ── */}
            <div className="notes-list-panel">
                <div className="notes-list-header">
                    <button className="notes-mobile-back" onClick={() => setMobilePanel('notebooks')}>
                        {backArrow} Notebooks
                    </button>
                    <button className="notes-add-btn" onClick={startNewNote}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Add {noun}
                    </button>
                    <button className="notes-list-sort-btn" title="Sort">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                    </button>
                </div>

                <div className="notes-list-search">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={`Search ${sectionTitle.toLowerCase()}…`}
                    />
                </div>

                <div className="notes-list-items">
                    {loading ? (
                        <div className="loading"><div className="spinner" /></div>
                    ) : filteredArticles.length === 0 ? (
                        <div className="notes-empty-list">
                            <div>{isRecipe ? '🍽️' : '📝'}</div>
                            <div>No {sectionTitle.toLowerCase()} yet</div>
                        </div>
                    ) : (
                        filteredArticles.map(a => {
                            const catInfo = flatCats.find(c => c.id === a.categoryId);
                            const dotColor = catInfo ? NOTE_COLORS[catInfo.colorIdx % NOTE_COLORS.length] : '#5C6070';
                            const isActive = activeNote?.id === a.id;
                            return (
                                <button
                                    key={a.id}
                                    className={`notes-list-item ${isActive ? 'active' : ''}`}
                                    onClick={() => openNote(a.id)}
                                >
                                    <span className="notes-list-dot" style={{ background: dotColor }} />
                                    <span className="notes-list-item-content">
                                        <span className="notes-list-item-title">{a.title}</span>
                                        <span className="notes-list-item-date">
                                            {new Date(a.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Panel 3: Note editor / viewer ── */}
            <div className="notes-editor-panel">
                {activeNoteLoading ? (
                    <div className="loading"><div className="spinner" /></div>
                ) : (editing || isNewNote) ? (
                    <div className="notes-editor-active">
                        <div className="notes-editor-topbar">
                            <button className="notes-mobile-back" onClick={() => { cancelEdit(); setMobilePanel('list'); }}>
                                {backArrow} Notes
                            </button>
                            <div className="notes-editor-meta-row">
                                <select
                                    className="notes-editor-cat-select"
                                    value={editCategoryId}
                                    onChange={e => setEditCategoryId(e.target.value)}
                                >
                                    <option value="">— Uncategorized —</option>
                                    {parentCats.map(p => (
                                        <React.Fragment key={p.id}>
                                            <option value={p.id}>{p.name}</option>
                                            {childrenOf(p.id).map(k => (
                                                <option key={k.id} value={k.id}>&nbsp;&nbsp;&nbsp;{k.name}</option>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </select>
                                <div className="notes-editor-actions">
                                    <button className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>Cancel</button>
                                    <button className="btn btn-primary btn-sm" onClick={() => saveNote()} disabled={saving}>
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </div>
                            {saveError && <div className="alert alert-error" style={{ marginTop: 8, marginBottom: 0 }}>{saveError}</div>}
                        </div>

                        <input
                            className="notes-title-input"
                            type="text"
                            value={editTitle}
                            onChange={e => { userEditedRef.current = true; setEditTitle(e.target.value); }}
                            placeholder={isRecipe ? 'Recipe name' : 'Note title'}
                            autoFocus
                        />
                        <div className="notes-editor-timestamp">
                            {isNewNote ? 'New note' : (activeNote?.updatedAt ? new Date(activeNote.updatedAt).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')}
                        </div>

                        <div className="notes-editor-body">
                            <RichTextEditor
                                value={editContent}
                                onChange={v => { userEditedRef.current = true; setEditContent(v); }}
                                placeholder={isRecipe ? 'Write the recipe…' : 'Start writing…'}
                                minHeight={420}
                            />
                        </div>
                    </div>
                ) : activeNote ? (
                    <div className="notes-viewer-active">
                        <div className="notes-viewer-topbar">
                            <button className="notes-mobile-back" onClick={() => setMobilePanel('list')}>
                                {backArrow} Notes
                            </button>
                            <div className="notes-viewer-actions">
                                <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(true); setSaveError(''); }}>Edit</button>
                                <button className="btn btn-secondary btn-sm" onClick={() => printArticle(activeNote, isRecipe)}>Print</button>
                                <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
                            </div>
                        </div>

                        <h1 className="notes-viewer-title">{activeNote.title}</h1>
                        <div className="notes-viewer-timestamp">
                            {activeNote.updatedAt ? new Date(activeNote.updatedAt).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                        <div className="notes-viewer-body rich-text-content"
                            dangerouslySetInnerHTML={{ __html: activeNote.content || '<p><em>This note is empty. Click Edit to start writing.</em></p>' }}
                        />
                    </div>
                ) : (
                    <div className="notes-empty-state">
                        <div className="notes-empty-icon">{isRecipe ? '🍽️' : '📝'}</div>
                        <div className="notes-empty-text">Select a {noun.toLowerCase()} or click Add {noun}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default KnowledgeBaseView;
